import React, { ReactElement, Suspense, useEffect, useRef, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ToastContainer, ErrorFallback, DefaultSpinner } from '@ocr-gateway/ui';
import {
  setMenu,
  Api,
  WSService,
  RoleChangeService,
  LANDING_PAGE_URL,
  navbarHeight,
  YoutrackWidget
} from '@ocr-gateway/shared';
import { useLocation } from 'react-router';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import TagManager from 'react-gtm-module';
import _ from 'lodash';
import { ContentWrapper, Navbar, Routes, Menu, HostnameWrapper } from './components';
import { useSupportWidget } from './hooks/useSupportWidget';
import { routes } from './constants';
import SessionTimeout from './components/SessionTimeout';
import ChangeRolePopup from './components/ChangeRolePopup';

const AppMenuAndContent = () => {
  const menu = useSelector((state: any) => state.layout.menu);
  const { organization } = useSelector((state: any) => state.auth.user);
  const dispatch = useDispatch();

  useSupportWidget({ orgName: organization?.name, isMenuExpanded: menu.isExpanded });

  /*
  const [autoPopulate, setAutoPopulate] = useState(false);

  React.useEffect(() => {
    const trackResubmitForm = (e: any) => {
      console.log({ element: e.target });
      if (
        e.target.closest('button.button_e6d2.createAnother__d5e5.heightS_d519.primary_b2ec') ||
        e.target.matches('button.button_e6d2.createAnother__d5e5.heightS_d519.primary_b2ec')
      )
        setAutoPopulate(prev => !prev);
    };
    YoutrackWidget.appendToDom({
      email,
      username: `${firstName} ${lastName}`,
      organization: organization.name,
      currentUrl: `${window.location.href}`,
      cb: () => {
        const resubmitButton = document.querySelector('.formPopup__bc9a');
        resubmitButton?.addEventListener('click', trackResubmitForm);
      }
    });
    return () => {
      document.removeEventListener('click', trackResubmitForm);
    };
  }, []);

  React.useEffect(() => {
    if (!_.isEmpty(email) && !_.isEmpty(organization?.name)) {
      YoutrackWidget.initYouTrackWidget({
        email,
        username: `${firstName} ${lastName}`,
        organization: organization.name,
        currentUrl: `${window.location.href}`
      });
    }
  }, [email, organization, window.location.href, autoPopulate]);
  */

  const setMenuExpanded = (isExpanded: boolean, width: string) => {
    dispatch(
      setMenu({
        isExpanded,
        width
      })
    );
  };

  const location = useLocation();
  useEffect(() => {
    document.getElementById('scroll').scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <Menu expanded={menu.isExpanded} setExpanded={setMenuExpanded} />
      <ContentWrapper expanded={menu.isExpanded}>
        <Suspense fallback={<DefaultSpinner />}>
          <Routes routes={routes} />
        </Suspense>
      </ContentWrapper>
      <ToastContainer />
    </>
  );
};

const App: React.FC = (props): ReactElement => {
  const { authWasInitialized, user, isSupportRole } = useSelector(
    (state: RootStateOrAny) => state.auth
  );
  const { pushInstruction } = useMatomo();
  const { enableLinkTracking } = useMatomo();
  const { trackPageView, trackEvent } = useMatomo();
  const dispatch = useDispatch();
  const { changeRoleWarningPopup } = useSelector((state: any) => state.popups);
  const userId = useSelector((state: any) => state.auth.user.id);

  React.useEffect(() => {
    Api.setDispatch({ dispatch });
    WSService.setDispatch({ dispatch });
    RoleChangeService.setDispatch({ dispatch });
  }, []);

  React.useEffect(() => {
    (async () => {
      try {
        if (userId) {
          const {
            id,
            firstName,
            lastName,
            organization: { id: orgId }
          } = user;

          await RoleChangeService.connect({
            orgId,
            userId: id,
            name: `${firstName} ${lastName}`,
            isSupport: isSupportRole,
            userDetails: user
          });
        }
      } catch (err) {
        console.log(err);
      }
    })();
  }, [userId, user.organization.id]);

  React.useEffect(() => {
    if (user.id) {
      const isInternalTraffic = user.email.endsWith('@urchinsys.com');

      pushInstruction('setUserId', user.email);
      trackPageView({
        customDimensions: [
          {
            id: 1,
            value: user.email
          },
          {
            id: 2,
            value: user.organization.name
          }
        ]
      });

      TagManager.dataLayer({
        dataLayer: {
          event: 'login',
          company: user.organization.name,
          email: user.email,
          ...(isInternalTraffic && { trafficType: 'internal' })
        }
      });
    }
  }, [user]);

  enableLinkTracking();

  return (
    <>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <HostnameWrapper>
          <Router>
            <Navbar logoUri={LANDING_PAGE_URL} height={navbarHeight} />
            <AppMenuAndContent />
          </Router>
          <SessionTimeout minutes={30} />
          {changeRoleWarningPopup.show && (
            <ChangeRolePopup minutes={1} show={changeRoleWarningPopup.show} isSimple />
          )}
        </HostnameWrapper>
        {/* <div
          id="youtrack-component"
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            zIndex: 9999
          }}
        ></div> */}
        {authWasInitialized && (
          <div
            dangerouslySetInnerHTML={{
              __html: `<pl-support-widget
                        style="visibility: hidden"
                        class="pl-ticket_bottom-left"
                        toggle-btn="<svg width='70' height='70' viewBox='0 0 70 70' fill='none' xmlns='http://www.w3.org/2000/svg'><rect width='70' height='70'/><g clip-path='url(#clip0_0_1)'><rect width='1920' height='1490' transform='translate(-16 -900)'/><rect width='235' height='1418' transform='translate(-16 -828)'/><g filter='url(#filter0_d_0_1)'><path d='M4 31C4 13.8792 17.8792 0 35 0C52.1208 0 66 13.8792 66 31C66 48.1208 52.1208 62 35 62H14C8.47715 62 4 57.5228 4 52V31Z' fill='#FF715B'/></g><path d='M39.8757 30.0391H26.7507V32.9557H39.8757V30.0391ZM26.7507 24.2057H44.2507V27.1224H26.7507V24.2057ZM20.9173 19.8307V46.0807L26.7507 40.2474H47.1673C47.9409 40.2474 48.6827 39.9401 49.2297 39.3931C49.7767 38.8461 50.084 38.1043 50.084 37.3307V19.8307C50.084 18.2266 48.7715 16.9141 47.1673 16.9141H23.834C23.0604 16.9141 22.3186 17.2214 21.7716 17.7683C21.2246 18.3153 20.9173 19.0572 20.9173 19.8307ZM23.834 19.8307H47.1673V37.3307H25.5402L23.834 39.037V19.8307Z' fill='white'/><path d='M 26.7507 24.2057 H 44.2507 V 27.1224 H 26.7507 V 24.2057 Z M 20.9173 19.8307 V 46.0807 L 26.7507 40.2474 H 47.1673 C 47.9409 40.2474 48.6827 39.9401 49.2297 39.3931 C 49.7767 38.8461 50.084 38.1043 50.084 37.3307 V 19.8307 C 50.084 18.2266 48.7715 16.9141 47.1673 16.9141 H 23.834 C 23.0604 16.9141 22.3186 17.2214 21.7716 17.7683 C 21.2246 18.3153 20.9173 19.0572 20.9173 19.8307 Z M 40 33 H 26.7507 V 30 L 40 30 Z' fill='white'/></g><defs><filter id='filter0_d_0_1' x='0' y='0' width='70' height='70' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'><feFlood flood-opacity='0' result='BackgroundImageFix'/><feColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' result='hardAlpha'/><feOffset dy='4'/><feGaussianBlur stdDeviation='2'/><feComposite in2='hardAlpha' operator='out'/><feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0'/><feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_0_1'/><feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow_0_1' result='shape'/></filter><clipPath id='clip0_0_1'><rect width='1920' height='1490' fill='white' transform='translate(-16 -900)'/></clipPath></defs></svg>"
                        title-text="Have a question?"
                        file-input-label="browse"
                        file-input-label-prefix="Click to"
                        file-input-label-postfix="or drag files here"
                        client-id="b5ac042565f4a9bac98f238b169a8cfb"
                        client="${user.organization.name}"
                        email="${user.email}"
                        contact-name="${user.firstName} ${user.lastName}"
                      />`
            }}
          />
        )}
      </ErrorBoundary>
    </>
  );
};

export default App;
